import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, MessageSquare, CalendarCheck, CalendarX, Star, Handshake } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const POLL_MS = 30000;

const TYPE_ICONS = {
  message_received: MessageSquare,
  booking_requested: CalendarCheck,
  booking_accepted: CalendarCheck,
  booking_declined: CalendarX,
  booking_cancelled: CalendarX,
  review_received: Star,
  sale_closed: Handshake,
};

export default function NotificationsBell() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const panelRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count);
    } catch (err) {
      console.error("[notifications] fetch count failed", err);
    }
  };

  const fetchList = async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");
      setItems(data);
    } catch (err) {
      console.error("[notifications] fetch list failed", err);
    }
  };

  useEffect(() => {
    if (loading || !user) return;
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [user, loading]);

  useEffect(() => {
    if (!open) return;
    fetchList();
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (loading || !user) return null;

  const handleClick = async (n) => {
    if (!n.is_read) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
      } catch (err) {
        console.error("[notifications] mark read failed", err);
      }
    }
    setOpen(false);
    fetchCount();
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    try {
      await api.patch("/notifications/read-all");
      fetchCount();
      fetchList();
      toast.success(t("all_read"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      fetchList();
      fetchCount();
    } catch (err) {
      console.error("[notifications] delete failed", err);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen(v => !v)}
        className="relative text-[#1A1F1C]/70 hover:text-[#143122] transition-colors p-1"
        title={t("notifications")}
        aria-label="Notifications"
      >
        <Bell size={20} strokeWidth={1.5} />
        {count > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#143122] text-white text-[10px] font-semibold leading-4 text-center"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-3 w-[360px] max-w-[90vw] bg-white border border-[#E6E2D8] rounded-lg shadow-xl overflow-hidden"
          style={{ zIndex: 10002 }}
        >
          <div className="flex items-center justify-between p-3 border-b border-[#E6E2D8] bg-[#F4F1E9]">
            <div className="overline text-[#143122]">{t("notifications")}</div>
            {items.some(i => !i.is_read) && (
              <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-[#143122] hover:underline inline-flex items-center gap-1">
                <Check size={12} strokeWidth={1.75} /> {t("mark_all_read")}
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#1A1F1C]/60">{t("no_notifications")}</div>
            ) : items.map((n) => {
              const Icon = TYPE_ICONS[n.type] || Bell;
              return (
                <div
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClick(n)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(n); }}
                  className={`w-full text-left p-3 border-b border-[#E6E2D8] hover:bg-[#F4F1E9]/50 transition-colors flex items-start gap-3 cursor-pointer ${!n.is_read ? "bg-[#F4F1E9]/40" : ""}`}
                >
                  <div className={`mt-0.5 flex-shrink-0 ${!n.is_read ? "text-[#143122]" : "text-[#1A1F1C]/50"}`}>
                    <Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-sm font-medium ${!n.is_read ? "text-[#143122]" : "text-[#1A1F1C]"}`}>
                        {n.title}
                      </div>
                      {!n.is_read && <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#143122] flex-shrink-0" />}
                    </div>
                    {n.body && <div className="mt-0.5 text-xs text-[#1A1F1C]/70 line-clamp-2">{n.body}</div>}
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-[10px] text-[#1A1F1C]/50">
                        {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}
                      </span>
                      <button
                        data-testid={`notif-del-${n.id}`}
                        onClick={(e) => remove(e, n.id)}
                        className="text-[#1A1F1C]/40 hover:text-[#143122] transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
